import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Select, Switch, ConfirmDialog, ManagedDataTable } from '@/design-system';
import type { ColumnDef } from '@tanstack/react-table';
import { Trash2, RefreshCw, Plus } from 'lucide-react';
import { formatAmount } from './formatAmount';

/**
 * Editor genérico de preços por moeda (plan_prices / product_prices).
 *
 * Uma linha por moeda; cada linha mapeia para UMA Stripe Price (Orya é source of truth,
 * Stripe é espelho). O badge `syncStatus` reflete se a linha já foi materializada no
 * Stripe pelo worker de outbox.
 *
 * A regra de ouro de imutabilidade de Price permanece: editar `unitAmount` aqui apaga
 * `stripePriceId` no backend, e o worker cria uma NOVA Price no Stripe e arquiva a
 * antiga. Subscriptions existentes NUNCA migram automaticamente.
 *
 * Dois modos:
 *   - `remote`: usado em telas de edição de entidade já persistida. Cada ação
 *     dispara chamada ao backend (list/upsert/delete) imediatamente. Exibe o
 *     badge de sync com o Stripe.
 *   - `local`:  usado em telas de criação, quando a entidade ainda não tem ID.
 *     Trabalha 100% em memória via `value`/`onChange`; não existe sync status.
 */

export interface PriceRow {
  id: string;
  currency: string;
  unitAmount: number;
  isDefault: boolean;
  stripePriceId: string | null;
  syncStatus: 'SYNCED' | 'PENDING';
}

export interface DraftPrice {
  currency: string;
  unitAmount: number;
  isDefault: boolean;
}

const DEFAULT_CURRENCIES = ['BRL', 'USD', 'EUR'];

type CommonProps = {
  supportedCurrencies?: string[];
  className?: string;
  heading?: string;
};

type RemoteProps = CommonProps & {
  mode: 'remote';
  listPrices: () => Promise<PriceRow[]>;
  upsertPrice: (body: {
    currency: string;
    unitAmount: number;
    isDefault?: boolean;
  }) => Promise<PriceRow>;
  deletePrice: (priceId: string) => Promise<void>;
};

type LocalProps = CommonProps & {
  mode: 'local';
  value: DraftPrice[];
  onChange: (next: DraftPrice[]) => void;
};

type Props = RemoteProps | LocalProps;

const EMPTY_LOCAL_DRAFTS: DraftPrice[] = [];

export function PricesEditor(props: Props) {
  const supportedCurrencies = props.supportedCurrencies ?? DEFAULT_CURRENCIES;
  const heading = props.heading;
  const localDrafts = props.mode === 'local' ? props.value : EMPTY_LOCAL_DRAFTS;

  // Fonte de verdade das linhas: estado remoto vem do backend; local vem do pai.
  const [remoteRows, setRemoteRows] = useState<PriceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<PriceRow | null>(null);
  const [editingRows, setEditingRows] = useState<Record<string, { amount: string; isDefault: boolean }>>({});

  const rows = useMemo<PriceRow[]>(() => {
    if (props.mode === 'remote') return remoteRows;
    return localDrafts.map<PriceRow>((d, i) => ({
      id: `local-${i}-${d.currency}`,
      currency: d.currency,
      unitAmount: d.unitAmount,
      isDefault: d.isDefault,
      stripePriceId: null,
      syncStatus: 'PENDING',
    }));
  }, [props.mode, remoteRows, localDrafts]);

  const reload = useCallback(async () => {
    if (props.mode !== 'remote') return;
    setLoading(true);
    setError(null);
    try {
      const data = await props.listPrices();
      setRemoteRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao listar preços');
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    if (props.mode === 'remote') void reload();
  }, [reload, props.mode]);

  const availableCurrencies = useMemo(
    () => supportedCurrencies.filter((c) => !rows.some((r) => r.currency === c)),
    [supportedCurrencies, rows],
  );

  const [newCurrency, setNewCurrency] = useState<string>(availableCurrencies[0] ?? '');
  const [newAmount, setNewAmount] = useState<string>('');
  const [newIsDefault, setNewIsDefault] = useState<boolean>(false);

  // Mantém `newCurrency` sempre dentro de `availableCurrencies`. Sem isso o <select>
  // exibe a primeira opção visível mas o state React guarda a moeda removida,
  // fazendo o "Adicionar" salvar na moeda errada (bug reportado: USD salvava como BRL).
  useEffect(() => {
    if (!newCurrency || !availableCurrencies.includes(newCurrency)) {
      setNewCurrency(availableCurrencies[0] ?? '');
    }
  }, [availableCurrencies, newCurrency]);

  async function handleUpsertRemote(
    currency: string,
    unitAmount: number,
    isDefault?: boolean,
  ) {
    if (props.mode !== 'remote') return;
    setSaving(true);
    setError(null);
    try {
      await props.upsertPrice({ currency, unitAmount, isDefault });
      await reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar preço');
      return false;
    } finally {
      setSaving(false);
    }
  }

  function handleUpsertLocal(
    currency: string,
    unitAmount: number,
    isDefault?: boolean,
  ) {
    if (props.mode !== 'local') return;
    const next = [...props.value];
    const idx = next.findIndex((d) => d.currency === currency);
    if (idx >= 0) {
      next[idx] = {
        ...next[idx],
        unitAmount,
        isDefault: isDefault ?? next[idx].isDefault,
      };
    } else {
      next.push({ currency, unitAmount, isDefault: isDefault ?? false });
    }
    if (isDefault === true) {
      for (let i = 0; i < next.length; i++) {
        next[i] = { ...next[i], isDefault: next[i].currency === currency };
      }
    }
    props.onChange(next);
  }

  async function handleSaveRow(
    row: PriceRow,
    unitAmount: number,
    isDefault?: boolean,
  ) {
    if (props.mode === 'remote') {
      return handleUpsertRemote(row.currency, unitAmount, isDefault);
    } else {
      handleUpsertLocal(row.currency, unitAmount, isDefault);
      return true;
    }
  }

  async function handleAdd() {
    const currency = newCurrency;
    if (!currency || !availableCurrencies.includes(currency)) {
      setError('Selecione uma moeda válida');
      return;
    }
    const parsed = Math.round(Number((newAmount || '').replace(',', '.')) * 100);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
      setError('Informe um valor válido');
      return;
    }
    // No modo local, exige que a primeira linha seja marcada como padrão se ainda
    // não houver default definido — assim o pai sempre tem um draft padrão.
    const forceDefault = rows.length === 0 ? true : newIsDefault;

    if (props.mode === 'remote') {
      if (!await handleUpsertRemote(currency, parsed, forceDefault)) return;
    } else {
      handleUpsertLocal(currency, parsed, forceDefault);
    }
    setNewAmount('');
    setNewIsDefault(false);
    setError(null);
  }

  function handleDeleteRequest(row: PriceRow) {
    if (row.isDefault) {
      setError('Não é possível remover o preço da moeda padrão.');
      return;
    }
    setPendingDeleteRow(row);
  }

  async function executePendingDelete() {
    const row = pendingDeleteRow;
    if (!row) return;
    if (props.mode === 'remote') {
      setSaving(true);
      setError(null);
      try {
        await props.deletePrice(row.id);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erro ao remover preço');
      } finally {
        setSaving(false);
      }
    } else {
      props.onChange(props.value.filter((d) => d.currency !== row.currency));
    }
    setPendingDeleteRow(null);
  }

  function cancelEdit(currency: string) {
    setEditingRows((previous) => {
      const next = { ...previous };
      delete next[currency];
      return next;
    });
  }

  async function saveEdit(row: PriceRow) {
    const draft = editingRows[row.currency];
    if (!draft) return;
    const amount = Math.round(Number(draft.amount.replace(',', '.')) * 100);
    if (!Number.isSafeInteger(amount) || amount <= 0) {
      setError('Informe um valor válido');
      return;
    }
    if (await handleSaveRow(row, amount, draft.isDefault)) cancelEdit(row.currency);
  }

  const columns: ColumnDef<PriceRow>[] = [
    { accessorKey: 'currency', header: 'Moeda', enableSorting: false, cell: ({ row }) => <span className="font-medium">{row.original.currency}</span> },
    { accessorKey: 'unitAmount', header: 'Valor', enableSorting: false, cell: ({ row }) => {
      const draft = editingRows[row.original.currency];
      return draft ? <Input aria-label={'Valor em ' + row.original.currency} inputMode="decimal" value={draft.amount} disabled={saving} onChange={(event) => setEditingRows((previous) => ({ ...previous, [row.original.currency]: { ...draft, amount: event.target.value } }))} /> : formatAmount(row.original.unitAmount, row.original.currency);
    } },
    { id: 'default', header: 'Padrão', enableSorting: false, cell: ({ row }) => {
      const draft = editingRows[row.original.currency];
      return draft ? <Switch label="Moeda padrão" checked={draft.isDefault} disabled={saving} onChange={(checked) => setEditingRows((previous) => ({ ...previous, [row.original.currency]: { ...draft, isDefault: checked } }))} /> : row.original.isDefault ? <Badge>Padrão</Badge> : <span className="text-gray-400">—</span>;
    } },
    ...(props.mode === 'remote' ? [{ id: 'sync', header: 'Stripe', enableSorting: false, cell: ({ row }: { row: { original: PriceRow } }) => <SyncBadge status={row.original.syncStatus} stripePriceId={row.original.stripePriceId} /> }] : []),
    { id: 'actions', header: '', enableSorting: false, cell: ({ row }) => <div className="flex justify-end gap-2">
      {editingRows[row.original.currency] ? <>
        <Button type="button" size="sm" disabled={saving} onClick={() => void saveEdit(row.original)}>Salvar</Button>
        <Button type="button" size="sm" variant="secondary" disabled={saving} onClick={() => cancelEdit(row.original.currency)}>Cancelar</Button>
      </> : <>
        <Button type="button" size="sm" variant="soft" disabled={saving} onClick={() => setEditingRows((previous) => ({ ...previous, [row.original.currency]: { amount: (row.original.unitAmount / 100).toFixed(2).replace('.', ','), isDefault: row.original.isDefault } }))}>Editar</Button>
        <Button type="button" variant="ghost" size="sm" aria-label={'Remover preço em ' + row.original.currency} disabled={saving || row.original.isDefault} onClick={() => handleDeleteRequest(row.original)}><Trash2 className="text-red-600" /></Button>
      </>}
    </div> },
  ];

  return <div className={props.className}>
    {error && <p role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    <ManagedDataTable
      appearance="embedded"
      columns={columns} data={rows} title={heading} disableSearch showPagination={false}
      getRowId={(row) => row.currency} loading={loading} emptyMessage="Nenhum preço cadastrado ainda."
      toolbarButtons={props.mode === 'remote' ? [{ label: 'Atualizar preços', icon: <RefreshCw />, variant: 'subtle', onClick: () => void reload() }] : []}
      footer={availableCurrencies.length > 0 ? <div className="grid items-center gap-3 sm:grid-cols-[80px_minmax(100px,1fr)_auto_auto]">
        <Select aria-label="Moeda a adicionar" value={newCurrency} disabled={saving || loading} onChange={(event) => setNewCurrency(event.target.value)}>
          {availableCurrencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
        </Select>
        <Input aria-label="Valor da nova moeda" inputMode="decimal" placeholder="0,00" value={newAmount} disabled={saving || loading} onChange={(event) => setNewAmount(event.target.value)} />
        <Switch label="Moeda padrão" checked={rows.length === 0 || newIsDefault} disabled={saving || loading || rows.length === 0} onChange={setNewIsDefault} />
        <Button type="button" size="sm" disabled={saving || loading || !newCurrency || !availableCurrencies.includes(newCurrency)} onClick={() => void handleAdd()}><Plus />Adicionar moeda</Button>
      </div> : undefined}
    />
    <ConfirmDialog isOpen={pendingDeleteRow != null} onClose={() => setPendingDeleteRow(null)} onConfirm={() => void executePendingDelete()} title="Remover preço" message={pendingDeleteRow ? 'Remover preço em ' + pendingDeleteRow.currency + '?' : ''} confirmText="Remover" cancelText="Cancelar" variant="danger" isLoading={saving} />
  </div>;
}

function SyncBadge({
  status,
  stripePriceId,
}: {
  status: 'SYNCED' | 'PENDING';
  stripePriceId: string | null;
}) {
  if (status === 'SYNCED' && stripePriceId) {
    return (
      <span
        title={stripePriceId}
        className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-100 text-green-800"
      >
        SYNCED
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800">
      PENDING
    </span>
  );
}
